/**
 * Примитивы разметки печатного отчёта — общий словарь для секций (задачи
 * 5–11). Опираются на классы, определённые в `shell.ts` (например `.card`,
 * `.card-h`, `.card-b`, `.val`, `.grp`, `.kv`, `.tag` с вариантами
 * `.t-ok`/`.t-warn`/`.t-bad`/`.t-mut`, `.lbl`, `.blk`, `.bar`) — список не
 * исчерпывающий, здесь только сборка HTML-строк поверх этих классов.
 */
import { scoreHex } from './tokens';
import { escapeHtml } from './escape';

export function card(a: { title: string; value?: string; body: string }): string {
  return (
    `<div class="card"><div class="card-h"><em></em><h2>${escapeHtml(a.title)}</h2>` +
    `${a.value ? `<span class="val">${a.value}</span>` : ''}</div>` +
    `<div class="card-b">${a.body}</div></div>`
  );
}

export function groupDivider(labelText: string): string {
  return `<div class="grp"><span>${escapeHtml(labelText)}</span><i></i></div>`;
}

export function kvRow(labelText: string, value: string): string {
  return `<div class="kv"><dt>${escapeHtml(labelText)}</dt><dd>${value}</dd></div>`;
}

const TAG_CLASS = { ok: 't-ok', warn: 't-warn', bad: 't-bad', muted: 't-mut' } as const;

export function tag(text: string, variant: keyof typeof TAG_CLASS = 'muted'): string {
  return `<span class="tag ${TAG_CLASS[variant]}">${escapeHtml(text)}</span>`;
}

/**
 * `number` в `XMLAnalysisResult` — приведение типа (`as XMLAnalysisResult`
 * на границе `toXMLAnalysisResult`, см. `components/details/analysis/
 * types.ts`), а не рантайм-гарантия: Zod-схема ответа хранит
 * `fullResult: z.unknown()`, между моделью и разметкой нет валидатора.
 * Документ открывается через `document.write` и наследует origin
 * приложения — значение, подставленное вместо числа, попадает в
 * исполняемый контекст, а не только в кривую вёрстку. Здесь и во всех
 * точках, где число идёт прямо в HTML (без прохода через `scoreSpan`/
 * `bar`), значение сначала проверяется через `safeNumber`.
 */
export function safeNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function scoreSpan(score: number): string {
  const n = safeNumber(score);
  if (n === null) return '';
  return `<span style="font-family:var(--mono);font-weight:600;color:${scoreHex(n)}">${n}</span>`;
}

/**
 * `null` означает «метрика была неприменима», а не «ноль баллов». Приведение
 * такого значения к 0 красит несостоявшийся провал в красный — в документе,
 * который клиент сохраняет у себя и который потом не исправить.
 *
 * Цвет — через `var(--fg3)` (объявлена в `shell.ts` из `COLORS.fgSubtle`), а
 * не литеральным хексом: `#6b7280` случайно содержит цифру `0`, что путает
 * прочерк с «нулём» при текстовом сравнении вывода.
 */
export function nullableScore(score: number | null | undefined, hint?: string): string {
  if (score == null) {
    return `<span style="color:var(--fg3)">—${
      hint ? ` <span style="font-size:10.5px">${escapeHtml(hint)}</span>` : ''
    }</span>`;
  }
  return scoreSpan(score);
}

export function label(text: string): string {
  return `<div class="lbl">${escapeHtml(text)}</div>`;
}

export function block(labelText: string, body: string): string {
  return `<div class="blk">${label(labelText)}${body}</div>`;
}

export function bar(score: number): string {
  const n = safeNumber(score);
  if (n === null) return '';
  return `<div class="bar"><i style="width:${n}%;background:${scoreHex(n)}"></i></div>`;
}
